import apiAgent from "../../api/apiAgent";
import { useEffect, useState } from "react";
import {
  PageContainer,
  PageContent,
  PageFooterBar,
} from "../../components/PageContainer";
import { Link } from "react-router-dom";
import { BreadCrumbs, TitleBar } from "../../components/TitleBar";
import { Button } from "../../components/Button";
import { ItemList } from "../../components/Lists/ItemLists";
import _ from "lodash";
import {
  isCardViewAtom,
  orderAtom,
  placeFilterAtom,
  sortByAtom,
  topicFilterAtom,
  useValidateModel,
} from "../../atoms";
import { tMsg } from "../../components/ToastNotifications";
import { checkTitle, removeTags } from "../../utils";
import {
  defaultFilterOption,
  SortMomentsMenu,
} from "./customisation/SortMomentsMenu";
import { isAfter, isBefore } from "date-fns";
import { SearchBar } from "./customisation/SearchBar";
import { PublishBannerByModel } from "../../components/Publishing";
import { useAtomValue } from "jotai";

const MomentIndex = ({
  modelName,
}: {
  modelName: "moment" | "landmark";
}): JSX.Element => {
  useValidateModel(modelName);

  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState<boolean>(true);

  const [search, setSearch] = useState<string>("");
  const sortBy = useAtomValue(sortByAtom);
  const order = useAtomValue(orderAtom);
  const topic = useAtomValue(topicFilterAtom);
  const place = useAtomValue(placeFilterAtom);

  const isCardView = useAtomValue(isCardViewAtom);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await apiAgent.DataType.getAll(modelName);

      // Parse title & remove quotes from string arrays
      const parsedData = response.data.map((item: any) => {
        return {
          ...item,
          topics: item.topics?.map((t: string) => t.replaceAll('"', "")),
          place: item.place?.map((p: string) => p.replaceAll('"', "")),
          cardTitle: removeTags(item.title),
        };
      });

      setData(parsedData);
    } catch (err) {
      console.log(err);
      tMsg.error("Error retrieving data");
      // navigate("/error"); // TODO: put this back after debugging
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [modelName, refresh]);

  const applySearchFilterAndSort = () => {
    const topicFiltered =
      topic.value === defaultFilterOption.value
        ? data
        : data.filter((item) => _.includes(item.topics || [], topic.value));

    const bothFiltered =
      place.value === defaultFilterOption.value
        ? topicFiltered
        : topicFiltered.filter((item) => {
            return _.includes(item.place || [], place.value);
          });

    const searchApplied =
      search.length < 1
        ? bothFiltered
        : bothFiltered.filter(
            (item) =>
              (item.date || "").toLowerCase().includes(search) ||
              (item.title || "").toLowerCase().includes(search)
          );

    switch (sortBy.value) {
      case "date":
        searchApplied.sort((a, b) => {
          const left = removeTags(a.date || "").replaceAll(/\W/g, "");
          const right = removeTags(b.date || "").replaceAll(/\W/g, "");
          if (order.value === "asc") {
            return left.toLowerCase() >= right.toLowerCase() ? 1 : -1;
          } else return left.toLowerCase() <= right.toLowerCase() ? 1 : -1;
        });
        break;
      case "alphabetical":
        searchApplied.sort((a, b) => {
          const left = removeTags(a.cardTitle || "").replaceAll(/\W/g, "");
          const right = removeTags(b.cardTitle || "").replaceAll(/\W/g, "");
          if (order.value === "asc") {
            return left.toLowerCase() >= right.toLowerCase() ? 1 : -1;
          } else return left.toLowerCase() <= right.toLowerCase() ? 1 : -1;
        });
        break;
      case "last modified":
        searchApplied.sort((a, b) => {
          const left = new Date(a.updated_at);
          const right = new Date(b.updated_at);
          if (order.value === "asc") return isBefore(left, right) ? 1 : -1;
          else return isAfter(left, right) ? 1 : -1;
        });
    }

    return searchApplied;
  };

  const sortedAndFilteredData = applySearchFilterAndSort();

  return (
    <PageContainer isLoading={isLoading}>
      <PageContent>
        <BreadCrumbs crumbs={[]} />
        <TitleBar title={`${checkTitle(modelName)}s`}>
          <SearchBar currentSearch={search} setSearchTerm={setSearch} />
        </TitleBar>
        <PublishBannerByModel modelName={modelName} />
        <SortMomentsMenu />
        <ItemList
          data={sortedAndFilteredData}
          modelName={modelName}
          refreshList={() => setRefresh(!refresh)}
          sortable={false}
          isCard={isCardView}
        />
      </PageContent>
      <PageFooterBar>
        <Link to={"create"}>
          <Button>
            <span>
              <i className="icon-plus mr-1 text-sm" /> Add new moment
            </span>
          </Button>
        </Link>
      </PageFooterBar>
    </PageContainer>
  );
};

export default MomentIndex;
